import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useAccount,
  useConnect,
  useDisconnect,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from 'wagmi';
import { injected } from 'wagmi/connectors';
import { parseUnits, formatUnits } from 'viem';
import { fetchApi } from '@/api/client';
import type { QuoteResponse, TokensResponse } from '@/api/types';
import {
  CHAIN_ID,
  DODOPMM_INTEGRATION,
  EXPLORER_BASE,
  CANONICAL_CUSDT,
  CANONICAL_CUSDC,
} from '@/config';
import { dodopmmIntegrationAbi, erc20Abi } from '@/abi/integration';

export function SwapPage() {
  const { address, isConnected, chainId } = useAccount();
  const { connect } = useConnect();
  const { disconnect } = useDisconnect();

  const tokensQuery = useQuery({
    queryKey: ['tokens-swap', CHAIN_ID],
    queryFn: () =>
      fetchApi<TokensResponse>(`/api/v1/tokens?chainId=${CHAIN_ID}&limit=200&includeDodoPool=true`),
    staleTime: 60_000,
  });

  const fallbackTokens = useMemo(
    () => [
      { address: CANONICAL_CUSDT, symbol: 'cUSDT', decimals: 6, name: 'Tether USD (Compliant)' },
      { address: CANONICAL_CUSDC, symbol: 'cUSDC', decimals: 6, name: 'USD Coin (Compliant)' },
    ],
    [],
  );

  const cTokens = useMemo(() => {
    const fromApi = (tokensQuery.data?.tokens || []).filter((t) =>
      (t.symbol || '').toLowerCase().startsWith('c'),
    );
    return fromApi.length > 0 ? fromApi : fallbackTokens;
  }, [tokensQuery.data, fallbackTokens]);

  const [tokenIn, setTokenIn] = useState(CANONICAL_CUSDT);
  const [tokenOut, setTokenOut] = useState(CANONICAL_CUSDC);
  const [amountInHuman, setAmountInHuman] = useState('100');

  const tokenInMeta = cTokens.find(
    (t) => t.address.toLowerCase() === tokenIn.toLowerCase(),
  );
  const tokenOutMeta = cTokens.find(
    (t) => t.address.toLowerCase() === tokenOut.toLowerCase(),
  );
  const decimalsIn = tokenInMeta?.decimals ?? 6;
  const decimalsOut = tokenOutMeta?.decimals ?? 6;

  const amountInWei = useMemo(() => {
    try {
      return parseUnits(amountInHuman || '0', decimalsIn);
    } catch {
      return 0n;
    }
  }, [amountInHuman, decimalsIn]);

  const quoteQuery = useQuery({
    queryKey: ['quote', tokenIn, tokenOut, amountInHuman, decimalsIn],
    enabled: amountInWei > 0n && tokenIn !== tokenOut,
    queryFn: () =>
      fetchApi<QuoteResponse>(
        `/api/v1/quote?chainId=${CHAIN_ID}&tokenIn=${tokenIn}&tokenOut=${tokenOut}&amountIn=${amountInWei.toString()}`,
      ),
    staleTime: 15_000,
  });

  const amountOutWei = useMemo(() => {
    const raw = quoteQuery.data?.amountOut;
    if (!raw) return 0n;
    try {
      return BigInt(raw);
    } catch {
      return 0n;
    }
  }, [quoteQuery.data?.amountOut]);

  const minOut = useMemo(() => (amountOutWei * 99n) / 100n, [amountOutWei]);
  const poolAddress = quoteQuery.data?.poolAddress as `0x${string}` | undefined;

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: tokenIn as `0x${string}`,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address ? [address, DODOPMM_INTEGRATION as `0x${string}`] : undefined,
    query: { enabled: Boolean(address && tokenIn) },
  });

  const needsApprove =
    address && allowance !== undefined && amountInWei > 0n && allowance < amountInWei;

  const { writeContract: writeApprove, data: approveHash, isPending: approvePending } =
    useWriteContract();
  const approveReceipt = useWaitForTransactionReceipt({ hash: approveHash });

  const { writeContract: writeSwap, data: swapHash, isPending: swapPending } = useWriteContract();
  const swapReceipt = useWaitForTransactionReceipt({ hash: swapHash });

  useEffect(() => {
    if (approveReceipt.isSuccess) void refetchAllowance();
  }, [approveReceipt.isSuccess, refetchAllowance]);

  const wrongChain = isConnected && chainId !== CHAIN_ID;

  return (
    <>
      <h1>Atomic swap (single hop)</h1>
      <p className="lead">
        Fetches a quote from the aggregation API, then (on Chain {CHAIN_ID}) approves{' '}
        <span className="mono">DODOPMMIntegration</span> and calls{' '}
        <span className="mono">swapExactIn</span> for the returned pool. Multi-hop routing is not
        available in the API; use sequential swaps if needed.
      </p>

      <div className="card" style={{ maxWidth: 520 }}>
        <div className="form-row">
          {!isConnected ? (
            <button
              type="button"
              className="primary"
              onClick={() => connect({ connector: injected() })}
            >
              Connect wallet
            </button>
          ) : (
            <>
              <span className="mono muted">{address}</span>
              <button type="button" className="ghost" onClick={() => disconnect()}>
                Disconnect
              </button>
            </>
          )}
        </div>
        {wrongChain && (
          <div className="err-box">Switch your wallet to Chain {CHAIN_ID} (DeFi Oracle Meta).</div>
        )}

        <div className="form-row">
          <label>
            Token in
            <select value={tokenIn} onChange={(e) => setTokenIn(e.target.value)}>
              {cTokens.map((t) => (
                <option key={t.address} value={t.address}>
                  {t.symbol} — {t.address.slice(0, 8)}…
                </option>
              ))}
            </select>
          </label>
          <label>
            Token out
            <select value={tokenOut} onChange={(e) => setTokenOut(e.target.value)}>
              {cTokens.map((t) => (
                <option key={t.address} value={t.address}>
                  {t.symbol} — {t.address.slice(0, 8)}…
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-row">
          <label>
            Amount in ({tokenInMeta?.symbol || 'token'})
            <input
              value={amountInHuman}
              onChange={(e) => setAmountInHuman(e.target.value)}
              inputMode="decimal"
            />
          </label>
        </div>

        {quoteQuery.isFetching && <p className="muted">Fetching quote…</p>}
        {quoteQuery.error && (
          <div className="err-box">{(quoteQuery.error as Error).message}</div>
        )}
        {quoteQuery.data?.error && <div className="err-box">{quoteQuery.data.error}</div>}

        {quoteQuery.data && !quoteQuery.data.error && (
          <div className="ok-box" style={{ marginBottom: '1rem' }}>
            <div>
              Est. out:{' '}
              <strong>
                {amountOutWei > 0n
                  ? formatUnits(amountOutWei, decimalsOut)
                  : quoteQuery.data.amountOut || '—'}
              </strong>{' '}
              (raw minOut with 1% slip: {minOut.toString()})
            </div>
            <div className="muted" style={{ marginTop: '0.35rem' }}>
              Pool:{' '}
              {poolAddress ? (
                <a href={`${EXPLORER_BASE}/address/${poolAddress}`} target="_blank" rel="noreferrer">
                  {poolAddress}
                </a>
              ) : (
                '—'
              )}
            </div>
            <div className="muted mono" style={{ marginTop: '0.35rem', fontSize: '0.75rem' }}>
              Integration: {DODOPMM_INTEGRATION}
            </div>
          </div>
        )}

        {isConnected &&
          !wrongChain &&
          poolAddress &&
          amountInWei > 0n &&
          amountOutWei > 0n &&
          allowance !== undefined && (
          <div className="form-row">
            {needsApprove ? (
              <button
                type="button"
                className="primary"
                disabled={approvePending}
                onClick={() =>
                  writeApprove({
                    address: tokenIn as `0x${string}`,
                    abi: erc20Abi,
                    functionName: 'approve',
                    args: [DODOPMM_INTEGRATION as `0x${string}`, amountInWei],
                  })
                }
              >
                {approvePending ? 'Approving…' : 'Approve token'}
              </button>
            ) : (
              <button
                type="button"
                className="primary"
                disabled={swapPending}
                onClick={() =>
                  writeSwap({
                    address: DODOPMM_INTEGRATION,
                    abi: dodopmmIntegrationAbi,
                    functionName: 'swapExactIn',
                    args: [poolAddress, tokenIn as `0x${string}`, amountInWei, minOut],
                  })
                }
              >
                {swapPending ? 'Swapping…' : 'Swap (swapExactIn)'}
              </button>
            )}
          </div>
        )}

        {approveHash && (
          <p className="muted mono" style={{ fontSize: '0.8rem' }}>
            Approve tx:{' '}
            <a href={`${EXPLORER_BASE}/tx/${approveHash}`} target="_blank" rel="noreferrer">
              {approveHash}
            </a>{' '}
            {approveReceipt.isSuccess ? '✓' : approveReceipt.isLoading ? '…' : ''}
          </p>
        )}
        {swapHash && (
          <p className="muted mono" style={{ fontSize: '0.8rem' }}>
            Swap tx:{' '}
            <a href={`${EXPLORER_BASE}/tx/${swapHash}`} target="_blank" rel="noreferrer">
              {swapHash}
            </a>{' '}
            {swapReceipt.isSuccess ? '✓' : swapReceipt.isLoading ? '…' : ''}
          </p>
        )}
      </div>
    </>
  );
}
